import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { fhir2omopClient, type MappingResponse } from "../api/client";

const EXAMPLE_BUNDLE = JSON.stringify(
  {
    resourceType: "Bundle",
    type: "collection",
    entry: [
      {
        resource: {
          resourceType: "Patient",
          id: "patient-001",
          gender: "female",
          birthDate: "1975-04-22",
        },
      },
      {
        resource: {
          resourceType: "Observation",
          id: "obs-001",
          status: "final",
          subject: { reference: "Patient/patient-001" },
          code: {
            coding: [{ system: "http://loinc.org", code: "718-7", display: "Hemoglobin" }],
          },
          valueQuantity: { value: 13.4, unit: "g/dL" },
          effectiveDateTime: "2025-08-15T10:30:00Z",
        },
      },
      {
        resource: {
          resourceType: "Condition",
          id: "cond-001",
          subject: { reference: "Patient/patient-001" },
          code: {
            coding: [
              {
                system: "http://fhir.de/CodeSystem/bfarm/icd-10-gm",
                code: "I10",
                display: "Essential hypertension",
              },
            ],
          },
          onsetDateTime: "2020-03-12",
        },
      },
    ],
  },
  null,
  2,
);

export default function SyncMapping() {
  const [input, setInput] = useState(EXAMPLE_BUNDLE);
  const [profileMode, setProfileMode] = useState<"off" | "sync">("off");

  const mapMutation = useMutation({
    mutationFn: async () => {
      const parsed = JSON.parse(input);
      return fhir2omopClient.mapBundle(parsed, profileMode);
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-nexyo-dark">Sync Mapping</h1>
        <p className="text-nexyo-muted mt-1">
          Paste a FHIR Bundle, click Map, inspect the OMOP records.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <section className="nx-card">
          <div className="flex justify-between items-center mb-3">
            <h2 className="font-semibold">FHIR Input</h2>
            <button
              className="text-xs text-nexyo-primary hover:underline"
              onClick={() => setInput(EXAMPLE_BUNDLE)}
            >
              Reset to example
            </button>
          </div>
          <textarea
            className="w-full h-96 font-mono text-xs p-3 border border-gray-200 rounded resize-none bg-gray-50"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            spellCheck={false}
          />
        </section>

        <section className="nx-card">
          <h2 className="font-semibold mb-3">OMOP Output</h2>
          {mapMutation.isPending && <p className="text-nexyo-muted">Mapping...</p>}
          {mapMutation.isError && (
            <p className="text-nexyo-danger text-sm">
              {(mapMutation.error as Error).message}
            </p>
          )}
          {mapMutation.data && <OmopOutput data={mapMutation.data} />}
          {!mapMutation.data && !mapMutation.isPending && !mapMutation.isError && (
            <p className="text-nexyo-muted text-sm italic">No mapping yet. Click Map to start.</p>
          )}
        </section>
      </div>

      <section className="nx-card flex items-center gap-4 flex-wrap">
        <button
          className="nx-button-primary"
          disabled={mapMutation.isPending}
          onClick={() => mapMutation.mutate()}
        >
          {mapMutation.isPending ? "Mapping..." : "Map"}
        </button>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={profileMode === "sync"}
            onChange={(e) => setProfileMode(e.target.checked ? "sync" : "off")}
          />
          Sync-Profile via EDPS (slower, blocks)
        </label>
        <span className="text-xs text-nexyo-muted">
          Default = async event-bus profile, no latency impact.
        </span>
      </section>
    </div>
  );
}

function OmopOutput({ data }: { data: MappingResponse }) {
  const tables = Object.entries(data.omop_records);

  return (
    <div className="space-y-3 text-sm">
      {tables.map(([table, records]) => (
        <div key={table}>
          <h3 className="font-semibold text-nexyo-success">{table}</h3>
          <ul className="text-xs text-nexyo-muted mt-1 space-y-0.5">
            {records.map((r, i) => (
              <li key={i} className="font-mono">
                ▸ {Object.entries(r).slice(0, 4).map(([k, v]) => `${k}=${v}`).join("  ")}
                {Object.keys(r).length > 4 && " ..."}
              </li>
            ))}
          </ul>
        </div>
      ))}

      <details className="border-t pt-3 mt-3">
        <summary className="cursor-pointer text-xs text-nexyo-muted">
          mapping metadata · audit_id={data.mapping_metadata.audit_id}
        </summary>
        <pre className="text-xs bg-gray-50 p-2 rounded mt-2 overflow-x-auto">
          {JSON.stringify(data.mapping_metadata, null, 2)}
        </pre>
      </details>
    </div>
  );
}
